import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';

import {
  CarbonCreditTypeBadge,
  displayValue,
  formatComitenteDocument,
  isExternalUrl,
  resolveCarbonCreditTypeBadge,
} from '../shared/register-confirmation-details.utils';
import { RegisterConfirmationService } from '../service/register-confirmation.service';

@Component({
  standalone: false,
  selector: 'app-register-confirmation-details-drawer',
  templateUrl: './register-confirmation-details-drawer.component.html',
  styleUrls: ['./register-confirmation-details-drawer.component.scss'],
})
export class RegisterConfirmationDetailsDrawerComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() carbonCreditCode: string | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() loadFailed = new EventEmitter<void>();

  credit: CarbonCredit | null = null;
  isLoading = false;

  private loadSubscription?: Subscription;

  constructor(
    private readonly registerConfirmationService: RegisterConfirmationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true || changes['carbonCreditCode']) {
      this.loadDetailsIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  get typeBadge(): CarbonCreditTypeBadge {
    return resolveCarbonCreditTypeBadge(this.credit ?? ({} as CarbonCredit));
  }

  get issueDate(): string {
    return displayValue(this.credit?.issueDate);
  }

  get comitenteDocument(): string {
    return formatComitenteDocument(this.credit?.comitenteDocumentNumber);
  }

  get issuedQuantity(): string {
    return displayValue(this.credit?.creditInitialQuantity);
  }

  get currentQuantity(): string {
    return displayValue(this.credit?.creditQuantity);
  }

  get vintageStart(): string {
    return displayValue(this.credit?.vintageStart);
  }

  get vintageEnd(): string {
    return displayValue(this.credit?.vintageEnd);
  }

  get serialRange(): string {
    return displayValue(this.credit?.serialCode);
  }

  get projectLink(): string {
    return displayValue(this.credit?.projectLink);
  }

  get certifierName(): string {
    return displayValue(this.credit?.certifierDescription);
  }

  get additionalDescription(): string {
    return displayValue(this.credit?.observationText);
  }

  get hasProjectLink(): boolean {
    return isExternalUrl(this.credit?.projectLink);
  }

  get showBrcerBadge(): boolean {
    return this.credit?.brcerIndicator === true;
  }

  close(): void {
    this.closed.emit();
  }

  private loadDetailsIfNeeded(): void {
    if (!this.isOpen || !this.carbonCreditCode?.trim()) {
      return;
    }

    this.isLoading = true;
    this.credit = null;

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.registerConfirmationService
      .getCarbonCreditById(this.carbonCreditCode)
      .subscribe({
        next: credit => {
          this.credit = credit;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.loadFailed.emit();
        },
      });
  }
}
